import { createSlice } from "@reduxjs/toolkit";
import { getUserProfileAction } from "../../actions";
import {
  checkinDashboardAction,
  getIntercomTokenAction,
  trackerDashboardAction,
} from "../../actions/UserActions/DashboardAction";

interface IUser {
  id: string;
  name: string;
  email: string;
  address: string;
  avatar: string | null;
  createdAt: string;
  freeUser: string;
}

interface IMembership {
  _id: string;
  userId: string;
  planName: string;
  planId: string;
  planPrice: number;
  userEmail: string;
  type: string;
  freeScans: number;
  purchaseDate: string;
  expiryDate: string;
  credits: number;
  originalCredits: number;
  gbpConnections: number;
  citationAllowed: boolean;
  purchased_credits: number;
  status: string;
  addedBy: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  customerId: string;
  nextBillingDate: string;
  subscriptionId: string;
  ctr_booster_enabled: boolean;
  map_checkin_enabled: boolean;
  cancelAtPeriodEnd: boolean;
  nextCreditDate?: string;
  nextCreditAmount?: number;
}

interface IFeature {
  title: string;
  subFeatures: string[];
  _id: string;
}

interface IPlan {
  _id: string;
  planId: string;
  name: string;
  yearly: boolean;
  disabled: boolean;
  monthly_price: number;
  priceId_monthly: string;
  priceId_yearly: string;
  yearly_price: number;
  currency: string;
  category: string;
  credits: number;
  gbpConnections: number;
  citationAllowed: boolean;
  features: IFeature[];
  __v: number;
  createdAt: string;
  updatedAt: string;
}

interface IConfig {
  grid: { value: number; enabled: boolean };
  radius: { value: number; enabled: boolean };
  refresh: { frequency: string; refresh_every: number; enabled: boolean };
  refresh_hour: { value: number; enabled: boolean };
  _id: string;
  grid_array: Record<string, number>[];
  costPer100Credits: number;
  costPer100AiCredits?: number;
  costPerAiCall?: number;
  originalCostPerAiCall?: number;
  originalCostPer100AiCredits?: number;
  freeScans: number;
  per_pin_cost: number;
  redirect_url: string;
  setup_call_link?: string;
  setup_call_header?: string;
  setup_call_description?: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface IKeys {
  places_key: string;
  google_client_id: string;
}

interface IAddon {
  _id: string;
  addon_id: string;
  name: string;
  disabled: boolean;
  price: string;
  priceId: string;
  productId: string;
  currency: string;
  category: string;
  features: IFeature[];
  reseller_cost_cents?: number;
  reseller_product_id?: string;
  __v: number;
  createdAt: string;
  updatedAt: string;
}

export interface IWorkspaceMembership {
  _id?: string;
  role?: string;
  modules?: Record<string, boolean>;
  status?: string;
  allLocations?: boolean;
  isOwner?: boolean;
}

export interface IDashboardData {
  user: IUser;
  membership: IMembership;
  workspaceMembership?: IWorkspaceMembership;
  plans: IPlan[];
  configs: IConfig[];
  keys: IKeys;
  addons: IAddon[];
}

export interface ICancelSubscriptionData {
  subscriptionId: string;
  cancelAt: string;
}

export type Score = {
  high: number;
  mid: number;
  low: number;
  not_ranking: number;
  avg: number;
  marketShare: number;
};
type Integration = {
  id: string;
  type: string;
};

type CheckinBusiness = {
  business_id: string;
  business_name: string;
  address: string;
  reviews: number;
  rating: number;
  total_posts: number;
  widget_count: number;
  integrations: Integration[];
  website_widget_posts: number;
  gbp_posts: number;
  youtube_posts: number;
  facebook_post: number;
  instagram_post: number;
};

export type Business = {
  _id: string;
  business_name: string;
  formatted_address: string;
  rating: number;
  business_id: string;
  keyword_count: number;
  score: Score;
};

export type Pagination = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

export type BusinessesData = {
  businesses: Business[];
  pagination: Pagination | null;
};
export type CheckinBusinessesData = {
  businesses: CheckinBusiness[];
  pagination: Pagination | null;
};

export interface IIntercomData {
  hash: string;
  userId: string;
  name: string;
  email: string;
  workspaceName: string | null;
  businessNames: string;
}

interface IDashboardState {
  loading: boolean;
  dashboardData: IDashboardData | null;
  totalCredits: number;
  dashboardBusinessesdata: BusinessesData;
  dashboardBusinessesLoading: boolean;
  checkinDashboardData: CheckinBusinessesData;
  checkinDashboardLoading: boolean;
  intercomToken: string | null;
  intercomData: IIntercomData | null;
}

const initialState: IDashboardState = {
  loading: false,
  dashboardData: null,
  totalCredits: 0,
  dashboardBusinessesdata: {
    businesses: [],
    pagination: null,
  },
  dashboardBusinessesLoading: false,
  checkinDashboardData: {
    businesses: [],
    pagination: null,
  },
  checkinDashboardLoading: false,
  intercomToken: null,
  intercomData: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    //get user profile
    builder.addCase(getUserProfileAction.pending, (state) => {
      state.loading = true;
      // state.dashboardData = null;
    });
    builder.addCase(getUserProfileAction.fulfilled, (state, action) => {
      state.loading = false;
      state.dashboardData = action.payload.data;
      const avalibleCredits = action.payload.data?.membership?.credits || 0;
      const purchaseCredits =
        action.payload.data?.membership?.purchased_credits || 0;
      state.totalCredits = avalibleCredits + purchaseCredits;
    });
    builder.addCase(getUserProfileAction.rejected, (state) => {
      state.loading = false;
      // state.dashboardData = null;
    });

    builder
      .addCase(trackerDashboardAction.pending, (state) => {
        state.dashboardBusinessesLoading = true;
      })
      .addCase(trackerDashboardAction.fulfilled, (state, action) => {
        state.dashboardBusinessesLoading = false;
        state.dashboardBusinessesdata = action.payload.data;
      })
      .addCase(trackerDashboardAction.rejected, (state) => {
        state.dashboardBusinessesLoading = false;
      });

    builder
      .addCase(checkinDashboardAction.pending, (state) => {
        state.checkinDashboardLoading = true;
      })
      .addCase(checkinDashboardAction.fulfilled, (state, action) => {
        state.checkinDashboardLoading = false;
        state.checkinDashboardData = action.payload.data;
      })
      .addCase(checkinDashboardAction.rejected, (state) => {
        state.checkinDashboardLoading = false;
      });
    builder.addCase(getIntercomTokenAction.fulfilled, (state, action) => {
      state.intercomToken = action.payload.hash;
      state.intercomData = action.payload.success ? action.payload : null;
    });
  },
});

export default dashboardSlice.reducer;
